import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Star, Package, Truck, Shield, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCart } from '../contexts/CartContext';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedMetalType, setSelectedMetalType] = useState(null);
  
  const { addToCart, formatPrice } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`);
      
      if (!response.ok) {
        throw new Error('Product not found');
      }
      
      const productData = await response.json();
      setProduct(productData);
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      // Check if metal type is required but not selected
      if (product.has_metal_options && product.metal_options && product.metal_options.length > 0 && !selectedMetalType) {
        alert('Please select a metal type');
        return;
      }
      addToCart(product, quantity, selectedMetalType);
    }
  };

  const handleCallForPrice = () => {
    window.location.href = 'tel:+61886724588'; // (08) 8672-4588
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The product you are looking for does not exist.'}</p>
          <Button asChild>
            <Link to="/opal-store">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Store
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const price = parseFloat(product.price || product.regular_price || 0);
  const regularPrice = parseFloat(product.regular_price || 0);
  const salePrice = product.sale_price ? parseFloat(product.sale_price) : null;
  const isOnSale = salePrice && salePrice < regularPrice;
  const displayPrice = isOnSale ? salePrice : price;

  const images = product.images || [];
  const primaryImage = images.length > 0 ? images[selectedImageIndex].src : "https://placehold.co/400/400";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-purple-600 hover:text-purple-700">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/opal-store" className="text-purple-600 hover:text-purple-700">Opal Store</Link>
            {product.categories && product.categories.length > 0 && (
              <>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">{product.categories[0].name}</span>
              </>
            )}
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button variant="outline" asChild className="mb-6">
          <Link to="/opal-store">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Store
          </Link>
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-square rounded-lg overflow-hidden bg-white shadow-lg">
              <img
                src={primaryImage}
                alt={product.name}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = "https://placehold.co/400/400";
                }}
              />
            </div>
            
            {/* Thumbnail Images */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {images.slice(0, 4).map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 transition-all bg-white ${
                      selectedImageIndex === index 
                        ? 'border-purple-500 ring-2 ring-purple-200' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Product Header */}
            <div>
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
                  {product.sku && (
                    <p className="text-sm text-gray-500 font-mono">SKU: {product.sku}</p>
                  )}
                </div>
                {product.featured && (
                  <Badge className="bg-purple-600">
                    <Star className="w-3 h-3 mr-1" />
                    Featured
                  </Badge>
                )}
              </div>

              {/* Categories */}
              {product.categories && product.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.categories.map((category) => (
                    <Badge key={category.id} variant="outline" className="text-purple-600 border-purple-200">
                      {category.name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                {isOnSale ? (
                  <>
                    <span className="text-3xl font-bold text-purple-600">
                      {formatPrice(salePrice)}
                    </span>
                    <span className="text-xl text-gray-500 line-through">
                      {formatPrice(regularPrice)}
                    </span>
                    <Badge className="bg-red-600">SALE</Badge>
                  </>
                ) : displayPrice > 0 ? (
                  <span className="text-3xl font-bold text-purple-600">
                    {formatPrice(displayPrice)}
                  </span>
                ) : (
                  <div>
                    <span className="text-2xl font-bold text-orange-600">Call for Price</span>
                    <p className="text-sm text-gray-600 mt-1">Contact us for pricing on this unique piece</p>
                  </div>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2 mb-6">
                <Package className="w-5 h-5 text-green-600" />
                <span className="text-green-600 font-medium">In Stock</span>
                {product.stock_quantity && (
                  <span className="text-gray-500">({product.stock_quantity} available)</span>
                )}
              </div>
            </div>

            {/* Description */}
            {product.description && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3">Description</h3>
                  <div 
                    className="prose prose-sm text-gray-600"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Add to Cart / Call for Price */}
            <div className="space-y-4">
              {/* Metal Type Selection */}
              {product.has_metal_options && product.metal_options && product.metal_options.length > 0 && (
                <Card className="border-2 border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Select Metal Type *</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {product.metal_options.includes('sterling_silver') && (
                        <button
                          onClick={() => setSelectedMetalType('sterling_silver')}
                          className={`p-3 border-2 rounded-lg text-left transition-all ${
                            selectedMetalType === 'sterling_silver'
                              ? 'border-purple-600 bg-purple-100'
                              : 'border-gray-300 hover:border-purple-400'
                          }`}
                        >
                          <div className="font-semibold">Sterling Silver</div>
                        </button>
                      )}
                      {product.metal_options.includes('gold_plated') && (
                        <button
                          onClick={() => setSelectedMetalType('gold_plated')}
                          className={`p-3 border-2 rounded-lg text-left transition-all ${
                            selectedMetalType === 'gold_plated'
                              ? 'border-purple-600 bg-purple-100'
                              : 'border-gray-300 hover:border-purple-400'
                          }`}
                        >
                          <div className="font-semibold">Sterling Silver Gold Plated</div>
                          <div className="text-sm text-gray-600">Gold plated over 925 Sterling Silver</div>
                        </button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {displayPrice > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="flex items-center border rounded-lg">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-3"
                    >
                      -
                    </Button>
                    <span className="px-4 py-2 border-x">{quantity}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setQuantity(quantity + 1)}
                      className="px-3"
                    >
                      +
                    </Button>
                  </div>
                  <Button 
                    onClick={handleAddToCart}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white h-12 text-lg"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart - {formatPrice(displayPrice * quantity)}
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={handleCallForPrice}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12 text-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call for Price - (08) 8672-4588
                </Button>
              )}
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
                <div>
                  <div className="font-medium text-green-900">Authentic</div>
                  <div className="text-xs text-green-600">100% Australian</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Truck className="w-5 h-5 text-blue-600" />
                <div>
                  <div className="font-medium text-blue-900">Shipping</div>
                  <div className="text-xs text-blue-600">Australia Wide</div>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
                <div>
                  <div className="font-medium text-purple-900">Quality</div>
                  <div className="text-xs text-purple-600">Premium Grade</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;